<template>
	<div v-if="(isUserAllowedInLevel5or6 || isUserAllowedInLevel2) && isAuthenticatedUserInGroupAdmin">
		<div v-if="!isUserAllowedInLevel2">
			<BaseHeader :title="$t('forms').toString()"></BaseHeader>
			<div class="main">
				<div v-if="textTemplates && textTemplates.length">
					<div class="flex flex-col">
						<div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
							<div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
								<div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
									<table class="min-w-full divide-y divide-gray-200">
										<thead>
											<tr>
												<th class="px-6 py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider">
													{{ $t('name') }}
												</th>
											</tr>
										</thead>
										<tbody class="bg-white divide-y divide-gray-200">
											<tr
												class="hover:bg-gray-300 cursor-pointer text-left"
												v-for="text_template in textTemplates"
												:key="text_template.id"
												@click="redirectToTarget(text_template.id)"
											>
												<td class="px-6 py-4">
													{{ text_template.pretty_name }}
												</td>
												<td class="px-6 py-4 text-right">
													<font-awesome-icon :icon="['fas', 'chevron-right']" class="cursor-pointer" />
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-else-if="!textTemplates" class="flex flex-grow justify-center w-full opacity-75">
					<LoadingSpinner />
				</div>

				<div v-else>{{ $t('no_entries_found') }}</div>
			</div>
		</div>
		<div v-else>
			<BaseHeader :title="$t('system_variables').toString()" />
			<div class="main">
				<div v-if="!isLoading">
					<div class="flex flex-col">
						<div class="-my-2 sm:-mx-6 lg:-mx-8">
							<div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8 rounded-lg">
								<div class="shadow border-b border-gray-200 rounded-lg">
									<table class="min-w-full divide-y divide-gray-200 rounded-lg">
										<thead class="bg-transparent">
											<tr>
												<th class=" w-full px-6 py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider">
													{{ $t('name') }}
												</th>
												<th class="px-6 py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
											</tr>
										</thead>
										<tbody class="divide-y divide-gray-200 overflow-visible">
											<tr class="text-left overflow-visible">
												<td class="px-6 py-4 rounded-lg">
													{{ $t('payment_due') }}
												</td>
												<td class="px-6 pt-4 text-right w-64 flex justify-end">
													<BaseSelect
														class="flex w-full"
														ref="BaseSelectPaymentDue"
														required="true"
														v-if="isUserAllowedInLevel2"
														:options="paymentDueOptions"
														v-model="paymentDueSelect"
														@input="saveChanges"
														:filter-only="true"
														:disabled="!isAuthenticatedUserInGroupAdmin"
														:allow-empty="false"
													/>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-else class="flex flex-grow justify-center w-full opacity-75">
					<LoadingSpinner />
				</div>
			</div>
		</div>

		<BaseHeader :title="$t('form_appendix').toString()" />
		<div class="main">
			<div v-if="!isLoading">
				<div class="flex flex-col">
					<div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
						<div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
							<div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
								<table class="min-w-full divide-y divide-gray-200">
									<thead>
										<tr>
											<th class=" w-full px-6 py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider">
												{{ $t('name') }}
											</th>
											<th class="px-6 py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
										</tr>
									</thead>
									<tbody class="bg-white divide-y divide-gray-200">
										<tr class="text-left" v-for="pdfAppendix in pdfAppendixes" :key="pdfAppendix.id">
											<td
												class="px-6 py-4"
												:class="pdfAppendix.isStored ? 'cursor-pointer' : ''"
												@click="pdfAppendix.isStored ? downloadFile(pdfAppendix.id) : ''"
											>
												{{ pdfAppendix.name }}
											</td>
											<td
												class="px-6 py-4 text-right"
												:class="pdfAppendix.isStored ? 'cursor-pointer' : ''"
												@click="pdfAppendix.isStored ? downloadFile(pdfAppendix.id) : ''"
											>
												<font-awesome-icon :icon="['fas', 'eye']" class="cursor-pointer" />
											</td>
											<td class="px-6 py-4 text-right">
												<Toggle :active="pdfAppendix.isEnabled" @click="changedState(pdfAppendix)" />
											</td>
											<td class="px-6 py-4 text-right" v-if="pdfAppendix.isEnabled">
												<DocumentFileUploader :instant-upload="false" @input="uploadDocument($event, pdfAppendix)" accept="application/pdf">
													<font-awesome-icon :icon="['fas', 'upload']" class="cursor-pointer" />
												</DocumentFileUploader>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="flex flex-grow justify-center w-full opacity-75">
				<LoadingSpinner />
			</div>
		</div>
	</div>
	<Forbidden v-else />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import RouteWatcher from '@/mixins/RouteWatcher';
import { PdfTemplatesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import Forbidden from '@/views/default/Forbidden.vue';
import Authentication from '@/mixins/Authentication';
import Toggle from '@/components/forms/Toggle.vue';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader.vue';
import PdfFormAppendixRepository from '@/api/repositories/PdfFormAppendixRepository';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import SystemSettingsRepository from '@/api/repositories/SystemSettingsRepository';

export default {
	name: 'Forms',
	components: { BaseSelect, DocumentFileUploader, Toggle, Forbidden, LoadingSpinner, BaseHeader },
	mixins: [RouteWatcher, Authentication, AppContainerMixin],
	data() {
		return {
			textTemplates: [],
			pdfAppendixes: [],
			isLoading: false,
			paymentDueOptions: [
				{ display: '14 Tage', value: '14_days' },
				{ display: '30 Tage', value: '30_days' },
				{ display: '60 Tage', value: '60_days' },
				{ display: '90 Tage', value: '90_days' },
			],
			paymentDueSelect: null,
			apiSystemSettings: {},
		};
	},
	methods: {
		onRouteChange() {
			this.isLoading = true;
			if ((this.isUserAllowedInLevel5or6 || this.isUserAllowedInLevel2) && this.isAuthenticatedUserInGroupAdmin) {
				if (!this.isUserAllowedInLevel2) {
					this.requestTextTemplates();
				} else {
					this.getSystemSettings();
				}
				this.getPdfAppendixes();
			}
		},
		getSuccessMessage(type) {
			switch (type) {
				case 'gtc_repair_offer':
					return this.$t('gtc_upload_successful', { type: 'Reparatur-Auftrag' }).toString();
				case 'gtc_damageassessor':
					return this.$t('gtc_upload_successful', { type: 'Gutachten-Auftrag' }).toString();
				case 'dunning_covering_note':
					return this.$t('dunning_upload_successful').toString();
			}
		},
		getExplanationMessage(type) {
			switch (type) {
				case 'gtc_repair_offer':
					return this.$t('gtc_explanation', { type: 'Reparatur-Auftrag' }).toString();
				case 'gtc_damageassessor':
					return this.$t('gtc_explanation', { type: 'Gutachten-Auftrag' }).toString();
				case 'dunning_covering_note':
					return this.$t('dunning_explanation').toString();
			}
		},
		redirectToTarget(id) {
			this.$router.push({
				name: 'forms.edit',
				params: { id: id },
			});
		},
		uploadDocument(event, appendix) {
			new PdfFormAppendixRepository()
				.updateAppendix(appendix.id, { isEnabled: appendix.isEnabled, pdfAppendix: event })
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.getSuccessMessage(appendix.type), 'success').then(_ => {
						this.getPdfAppendixes();
					});
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('gtc_upload_error').toString(), 'error');
				});
		},
		changedState(pdfAppendix) {
			pdfAppendix.isEnabled = !pdfAppendix.isEnabled;
			if (pdfAppendix.isStored) {
				if (pdfAppendix.isEnabled) {
					this.$swal({
						title: this.$t('general.attention').toString(),
						text: this.$t('gtc_uploaded_use').toString(),
						icon: 'warning',
						cancelButtonText: this.$t('general.no').toString(),
						showCancelButton: true,
						confirmButtonText: this.$t('general.yes').toString(),
					}).then(result => {
						if (result.value) {
							new PdfFormAppendixRepository()
								.changeState(pdfAppendix.id, { isEnabled: pdfAppendix.isEnabled })
								.then(response => {
									this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success').then(_ => {
										this.getPdfAppendixes();
									});
								})
								.catch(e => {
									console.log(e);
								});
						}
					});
				} else {
					new PdfFormAppendixRepository()
						.changeState(pdfAppendix.id, { isEnabled: pdfAppendix.isEnabled })
						.then(response => {
							this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success').then(_ => {
								this.getPdfAppendixes();
							});
						})
						.catch(e => {
							console.log(e);
						});
				}
			} else {
				if (pdfAppendix.isEnabled) {
					this.$swal(this.$t('general.attention').toString(), this.getExplanationMessage(pdfAppendix.type), 'warning').then(() => {});
				}
			}
		},
		requestTextTemplates() {
			PdfTemplatesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.pdfTemplatesGet()
				.then(response => {
					this.textTemplates = response.data.data;
				});
		},
		saveChanges() {
			new SystemSettingsRepository()
				.updateValue(this.apiSystemSettings.id, { paymentDeadline: this.paymentDueSelect.value })
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
				});
		},
		getSystemSettings() {
			new SystemSettingsRepository()
				.getValue()
				.then(response => {
					this.apiSystemSettings = response.data;
					this.paymentDueSelect = this.paymentDueOptions.find(element => element.value === response.data.payment_deadline);
				})
				.catch(e => {
					console.log(e);
				});
		},
		getPdfAppendixes() {
			new PdfFormAppendixRepository()
				.getAllAppendixes()
				.then(response => {
					this.pdfAppendixes = response.data.data;
					this.isLoading = false;
				})
				.catch(e => {
					console.log(e);
				});
		},
		downloadFile(id) {
			new PdfFormAppendixRepository().getAppendixFile(id).then(response => {
				const blob = new Blob([response.data], { type: 'application/pdf' });
				const name = response.headers['content-disposition'].split('filename=')[1];
				if (this.isAppContainer) {
					const reader = new FileReader();
					reader.onload = function() {
						const arrayBuffer = this.result;
						// convert the ArrayBuffer to a comma-separated string
						const dataString = new Uint8Array(arrayBuffer).join(',');
						const messageData = {
							fileName: name,
							data: dataString,
						};
						// send the data to Swift
						window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
					};
					reader.readAsArrayBuffer(blob);
				} else {
					const resolvingUrl = URL.createObjectURL(blob) + '#toolbar=1&navpanes=0&scrollbar=0';
					const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(resolvingUrl)}&title=${encodeURIComponent(name)}`;

					setTimeout(function() {
						window.open(viewerUrl, '_blank');
						window.URL.revokeObjectURL(viewerUrl);
					}, 200);
				}
			});
		},
	},
};
</script>

<style scoped></style>
